import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { HiCubeTransparent, HiHome } from "react-icons/hi";
import { HiLocationMarker } from "react-icons/hi";
import { HiChartBar } from "react-icons/hi";
import { HiCreditCard } from "react-icons/hi";
import { HiUser } from "react-icons/hi";
import { HiLogout } from "react-icons/hi";
import { HiClipboardList } from "react-icons/hi";

import {
  Sidebar,
  SidebarHeader,
  SidebarTitle,
  SidebarSubtitle,
  SidebarMenu as SidebarMenuWrapper,
  MenuItem,
  MenuIcon,
} from "./StyledComponents";
import { BaseUrl } from "../../BaseUrl";
import { useTranslation } from 'react-i18next';

const SidebarMenu = ({
  userName,
  activeSection,
  setActiveSection,
  handleLogout,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [userType, setUserType] = useState(null); // 1 = regular user, 2 = admin
  const [currentPlan, setCurrentPlan] = useState(null);
  const [currentStatus, setCurrentStatus] = useState(null);

  const token = localStorage.getItem("pflegeUserToken");

  const goTo = (section, path) => {
    setActiveSection(section);
    navigate(path);
  };

  /* ===== Get user type ===== */
  useEffect(() => {
    const storedUserType = localStorage.getItem("pflegeUsertype");
    setUserType(storedUserType);
    console.log("User Type from localStorage:", storedUserType); // Debug log
  }, []);

  useEffect(() => {
    if (!token || userType === "2") return;

    fetch(`${BaseUrl}stripe/current-subscription`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        setCurrentPlan(data?.planName?.toLowerCase() || null);
        setCurrentStatus(data?.status || null);
      })
      .catch((err) => console.error("Subscription error:", err));
  }, [token, userType]);

  /* ===== Conditions ===== */
  const isAdmin = userType === "2"; // Only show users menu for admins
  const isEnterprise = currentPlan === "enterprise";
  const isRegularUser = userType === "1";

  return (
    <Sidebar>
      <SidebarHeader>
        <SidebarSubtitle>{userName}</SidebarSubtitle>
      </SidebarHeader>

      <SidebarMenuWrapper>
        <MenuItem
          active={activeSection === "dashboard"}
          onClick={() => goTo("dashboard", "/dashboard")}
        >
          <MenuIcon /><HiHome />{t("sidebar.menu.dashboard")}
        </MenuItem>

        {/* 🔥 USERS: Only for admins (user_type: 2) */}
        {isAdmin && (
          <MenuItem
            active={activeSection === "users"}
            onClick={() => goTo("users", "/dashboard/users")}
          >
            <MenuIcon /><HiUser />{t("sidebar.menu.users")}
          </MenuItem>
        )}

        <MenuItem
          active={activeSection === "spots"}
          onClick={() => goTo("spots", "/dashboard/places")}
        >
          <MenuIcon /><HiLocationMarker />{t("sidebar.menu.places")}
        </MenuItem>

        {/* 🔥 LEADS: Admin OR Enterprise users */}
        {(isAdmin || (isEnterprise && currentStatus == "active")) && (
          <MenuItem
            active={activeSection === "leads"}
            onClick={() => goTo("leads", "/dashboard/leads")}
          >
            <MenuIcon /><HiChartBar />{t("sidebar.menu.leads")}
          </MenuItem>
        )}

        {/* 🔥 SUBSCRIPTION: Only for regular users (user_type: 1) */}
        {isRegularUser && (
          <MenuItem
            active={activeSection === "subscription"}
            onClick={() => goTo("subscription", "/dashboard/subscription")}
          >
            <MenuIcon /><HiCreditCard />{t("sidebar.menu.subscription")}
          </MenuItem>
        )}

        {/* 🔥 SUBSCRIPTION LIST: Only for admins (user_type: 2) */}
        {isAdmin && (
          <MenuItem
            active={activeSection === "subscription_list"}
            onClick={() => goTo("subscription_list", "/dashboard/subscription_list")}
          >
            <MenuIcon /><HiCreditCard />{t("sidebar.menu.subscription_list")}
          </MenuItem>
        )}

        {/* 🔥 ACTIVITY LOGS: Only for admins (user_type: 2) */}
        {isAdmin && (
          <MenuItem
            active={activeSection === "activity-logs"}
            onClick={() => goTo("activity-logs", "/dashboard/activity-logs")}
          >
            <MenuIcon /><HiClipboardList /> {t("sidebar.menu.activity_logs")}
          </MenuItem>
        )}

        <MenuItem
          active={activeSection === "profile"}
          onClick={() => goTo("profile", "/dashboard/profile")}
        >
          <MenuIcon /><HiUser /> {t("sidebar.menu.profile")}
        </MenuItem>

        <MenuItem onClick={handleLogout}>
          <MenuIcon /><HiLogout /> {t("sidebar.menu.logout")}
        </MenuItem>
      </SidebarMenuWrapper>
    </Sidebar>
  );
};

export default SidebarMenu;